import { useEffect, useRef } from 'react';

import { useOktaAuth } from '@okta/okta-react';

export const OktaRefresherComponent = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const workerRef = useRef(null);
  const renewInProgress = useRef(false);

  useEffect(() => {
    if (window.Worker && authState?.isAuthenticated && !workerRef.current) {
      workerRef.current = new Worker(`${process.env.PUBLIC_URL}/okta-timer-worker.js`);

      workerRef.current.onerror = (error) => {
        console.error('okta refresh worker failed', error);
      };

      workerRef.current.onmessage = async (event) => {
        if (event.data?.type === 'WORKER_STARTED') {
          console.log(`Okta refresh worker started. Interval: ${event.data?.interval}ms`);

          return;
        }

        if (event.data === 'TICK_REFRESH') {
          if (renewInProgress.current) return;

          renewInProgress.current = true;
          console.log('Background worker triggered token refresh sequence...');

          try {
            const currentTokens = oktaAuth.tokenManager.getTokensSync();
            const { idToken, refreshToken } = currentTokens;

            if (idToken?.claims?.aud && idToken.claims.aud !== oktaAuth.options.clientId) {
              console.error('Okta token client mismatch. Clearing stale tokens.', {
                configuredClientId: oktaAuth.options.clientId,
                tokenAudience: idToken.claims.aud,
              });
              oktaAuth.tokenManager.clear();

              return;
            }

            if (!refreshToken) {
              console.error('Refresh token is not available for renewal');

              return;
            }

            const renewedTokens = await oktaAuth.token.renewTokens();

            oktaAuth.tokenManager.setTokens({
              ...renewedTokens,
              refreshToken: renewedTokens.refreshToken || refreshToken,
            });

            console.log('Tokens successfully rotated in the background');
          } catch (e) {
            console.log('Error while renewing tokens:', e);
          } finally {
            renewInProgress.current = false;
          }
        }
      };

      workerRef.current.postMessage('START_TIMER');

      return () => {
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
      };
    }
  }, [authState?.isAuthenticated, oktaAuth]);

  return null;
};
